import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';

// Reintento de escritas idempotentes. Uma falha SEM resposta (rede/timeout), um 5xx ou um
// 401 (refresh ja falhou no interceptor) sao transitorios: valem retentativa. Um 4xx e recusa
// definitiva de negocio (ex.: banca ja possui FALTA) e NAO deve ser reintentado.
const RETRY_BASE_MS = 500;
const RETRY_MAX = 3;

function isTransitorio(error: unknown): boolean {
  const e = error as { response?: { status?: number } } | undefined;
  const status = e?.response?.status;
  if (status == null) return true;
  if (status === 401) return true;
  return status >= 500;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function postComRetry<T>(fn: () => Promise<T>, max = RETRY_MAX): Promise<T> {
  let tentativa = 0;
  for (;;) {
    try {
      return await fn();
    } catch (error) {
      if (tentativa >= max || !isTransitorio(error)) throw error;
      const backoff = RETRY_BASE_MS * 2 ** tentativa + Math.floor(Math.random() * 200);
      await sleep(backoff);
      tentativa += 1;
    }
  }
}

type Status = 'pago' | 'falta' | 'nenhum' | 'divida' | 'pago_com_atraso' | 'pago_com_desconto' | 'pago_desconto' | 'pendente';

type StatusMap = Record<string, Status>; // key: bancaId as string

interface CobrancaStatusContextValue {
  cobrancaId: number | null;
  statuses: StatusMap;
  /** Bancas cuja escrita ainda nao foi confirmada pelo servidor (POST em voo). */
  pendingBancas: Record<string, true>;
  /** true enquanto houver qualquer escrita nao confirmada. */
  hasPendingWrites: boolean;
  ensureLoaded: (cobrancaId: number, feiraId?: number) => Promise<void>;
  getStatus: (bancaId: number) => Status;
  setStatus: (bancaId: number, status: Status, opts?: { image?: string; takenAt?: string }) => Promise<void>;
  flushBuffered: () => Promise<void>;
}

const CobrancaStatusContext = createContext<CobrancaStatusContextValue | null>(null);

export function CobrancaStatusProvider({ children }: { children: React.ReactNode }) {
  const [cobrancaId, setCobrancaId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<StatusMap>({});
  const [pendingBancas, setPendingBancas] = useState<Record<string, true>>({});

  // Ultimo estado confirmado pelo servidor. Base da reversao quando um POST falha —
  // desfazer nao pode depender de rede, senao um erro fica pintado como registro valido.
  const confirmedRef = useRef<StatusMap>({});
  // Status otimistas cuja escrita ainda nao foi confirmada, reaplicados por cima da
  // resposta do servidor para que um GET concorrente nao apague o que o cobrador acabou de marcar.
  const pendingRef = useRef<StatusMap>({});
  // Numero de sequencia dos GETs: resposta fora de ordem e descartada.
  const loadSeqRef = useRef(0);
  // Incrementa a cada mutacao concluida; invalida GET emitido antes dela.
  const mutationSeqRef = useRef(0);
  const loadingRef = useRef<{ promise: Promise<void>; mutationSeq: number } | null>(null);
  // Escritas que esgotaram os retries por falha transitoria (rede caida). Ficam pendentes
  // (pulsando) e sao reenviadas quando a conexao volta — garante persistencia a longo prazo.
  const falhasRef = useRef<Record<string, { status: Status; opts?: { image?: string; takenAt?: string } }>>({});

  const syncPendingState = useCallback(() => {
    const next: Record<string, true> = {};
    Object.keys(pendingRef.current).forEach(key => { next[key] = true; });
    setPendingBancas(next);
  }, []);

  const ensureLoaded = useCallback(async (nextCobrancaId: number, feiraId?: number) => {
    const inflight = loadingRef.current;
    // Reaproveita a requisicao em voo apenas se ela foi emitida DEPOIS da ultima mutacao.
    // Caso contrario ela nao enxerga o registro recem gravado e nao serve como revalidacao.
    if (inflight && inflight.mutationSeq === mutationSeqRef.current) {
      await inflight.promise;
      return;
    }

    const seq = ++loadSeqRef.current;
    // Numero de mutacoes confirmadas no momento do disparo. Se uma escrita for confirmada
    // DEPOIS deste GET, a resposta (anterior a ela) e obsoleta e apagaria o registro recem
    // gravado — descartada. Antes isso era garantido pelo refetch por clique; agora que o
    // registro confia no estado confirmado local, esta guarda assume a protecao (teste AC-4).
    const issuedAtMutation = mutationSeqRef.current;
    const promise = (async () => {
      const q = feiraId ? `?feira_id=${feiraId}` : '';
      // Sempre busca do banco de dados, sem cache
      const { data } = await api.get<{ cobrancaId: number; bancas: StatusMap }>(`/api/v1/cobrancas/${nextCobrancaId}/status${q}`);
      // Resposta obsoleta: um GET mais novo ja respondeu, aplicar isto retrocederia o mapa.
      if (seq !== loadSeqRef.current) return;
      // Resposta anterior a uma mutacao ja confirmada: aplica-la apagaria o registro.
      if (issuedAtMutation !== mutationSeqRef.current) return;
      const doServidor = data.bancas || {};
      confirmedRef.current = doServidor;
      setCobrancaId(data.cobrancaId);
      setStatuses({ ...doServidor, ...pendingRef.current });
    })();

    const entry = { promise, mutationSeq: mutationSeqRef.current };
    loadingRef.current = entry;
    try {
      await promise;
    } finally {
      if (loadingRef.current === entry) {
        loadingRef.current = null;
      }
    }
  }, []);

  const getStatus = useCallback((bancaId: number): Status => {
    const s = statuses[String(bancaId)];
    return (s as Status) || 'nenhum';
  }, [statuses]);

  const setStatus = useCallback(async (bancaId: number, status: Status, opts?: { image?: string; takenAt?: string }) => {
    if (cobrancaId == null) return;

    // Optimistic update - mapeia para status do backend
    // 'pago_desconto' é usado internamente, mas o backend espera 'pago_desconto' também
    const backendStatus = status === 'pago_desconto' ? 'pago_desconto' : status;
    // Para o estado interno, usa 'pago_com_desconto' quando for desconto
    const internalStatus = status === 'pago_desconto' ? 'pago_com_desconto' : status;
    const key = String(bancaId);

    // A banca ja aparece pintada, mas fica marcada como pendente: enquanto o POST nao for
    // confirmado ela NAO pode contar como cobrada para liberar a finalizacao da cobranca.
    pendingRef.current = { ...pendingRef.current, [key]: internalStatus };
    syncPendingState();
    setStatuses(prev => ({ ...prev, [key]: internalStatus }));

    const limparPendencia = () => {
      const restante = { ...pendingRef.current };
      delete restante[key];
      pendingRef.current = restante;
      syncPendingState();
    };

    const body: Record<string, unknown> = { status: backendStatus };
    if (status === 'falta') {
      body.image = opts?.image;
      body.takenAt = opts?.takenAt;
    }

    try {
      await postComRetry(() => api.post(`/api/v1/cobrancas/${cobrancaId}/bancas/${bancaId}/status/`, body));

      confirmedRef.current = { ...confirmedRef.current, [key]: internalStatus };
      mutationSeqRef.current += 1;
      const { [key]: _resolvida, ...restoFalhas } = falhasRef.current;
      falhasRef.current = restoFalhas;
      limparPendencia();
      // O POST 2xx ja confirma a persistencia; o estado confirmado local pinta a banca.
      // NAO refazemos o GET de /status por registro: na rajada da Tabajara isso enfileirava
      // GETs O(N bancas)/N+1 e era o maior ofensor da lentidao (ver plano 2026-10-26).
    } catch (error: unknown) {
      if (isTransitorio(error)) {
        // Falha transitoria mesmo apos os retries (rede caida, timeout, 5xx, sessao a renovar):
        // a escrita NAO foi recusada, apenas nao chegou. Mantem a banca PENDENTE (o card segue
        // pulsando o tom do status), sem reverter e sem propagar erro. hasPendingWrites continua
        // true, entao a finalizacao permanece bloqueada e o registro nao e perdido silenciosamente.
        // Enfileira para reenvio automatico quando a conexao voltar (evento 'online').
        falhasRef.current = { ...falhasRef.current, [key]: { status, opts } };
        return;
      }
      // Rejeicao de negocio (4xx): o servidor recusou. Reverte pelo ultimo valor confirmado,
      // sem depender de rede, e propaga o erro para a UI exibir a mensagem.
      mutationSeqRef.current += 1;
      const { [key]: _recusada, ...restoRecusa } = falhasRef.current;
      falhasRef.current = restoRecusa;
      limparPendencia();
      setStatuses(prev => {
        const next = { ...prev };
        const confirmado = confirmedRef.current[key];
        if (confirmado) {
          next[key] = confirmado;
        } else {
          delete next[key];
        }
        return next;
      });
      throw error;
    }
  }, [cobrancaId, syncPendingState]);

  // Reenvia as escritas que falharam por rede. Cada uma volta pelo setStatus (optimistic +
  // retry + sincronizacao), entao um 4xx no reenvio reverte corretamente e uma nova falha
  // transitoria apenas reenfileira para a proxima reconexao.
  const reenviarPendentes = useCallback(async () => {
    const pendentes = Object.entries(falhasRef.current);
    for (const [key, payload] of pendentes) {
      // Remove antes de tentar; o proprio setStatus reenfileira se falhar de novo.
      const { [key]: _tentando, ...resto } = falhasRef.current;
      falhasRef.current = resto;
      try {
        await setStatus(Number(key), payload.status, payload.opts);
      } catch {
        // 4xx: setStatus ja reverteu e limpou. Nada a fazer aqui.
      }
    }
  }, [setStatus]);

  // Ao voltar a conexao, drena a fila de pendentes automaticamente.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => { void reenviarPendentes(); };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [reenviarPendentes]);

  const flushBuffered = useCallback(async () => {
    // Mantido para compatibilidade: drena a fila de pendentes sob demanda.
    await reenviarPendentes();
  }, [reenviarPendentes]);

  const value = useMemo<CobrancaStatusContextValue>(() => ({
    cobrancaId,
    statuses,
    pendingBancas,
    hasPendingWrites: Object.keys(pendingBancas).length > 0,
    ensureLoaded,
    getStatus,
    setStatus,
    flushBuffered,
  }), [cobrancaId, statuses, pendingBancas, ensureLoaded, getStatus, setStatus, flushBuffered]);

  return (
    <CobrancaStatusContext.Provider value={value}>
      {children}
    </CobrancaStatusContext.Provider>
  );
}

export function useCobrancaStatus() {
  const ctx = useContext(CobrancaStatusContext);
  if (!ctx) throw new Error('useCobrancaStatus must be used within CobrancaStatusProvider');
  return ctx;
}


